import {
	DIVISION_RECOMMENDER_OPTIONS,
	DocumentObjectListRefTableEnum,
	INFRASTRUCTURE_OPTIONS,
	MasterInfoEnum,
	OWNERSHIP_OPTIONS,
	UpdateHistoryRefTableNameEnum,
} from '@/constant';
import {
	GetAppUserInfoResponse,
	GetCommonApplicantListResponse,
	GetCommonAssignmentApplicantListResponse,
	GetCommonDocumentObjectListResponse,
	GetDocumentMappingsResponse,
	GetUpdateHistoryListResponse,
	IApplicantListItem,
	IAssignmentItem,
	IAutocompleteSuggestionItem,
	ICommonApplicant,
	ICommonAssignmentApplicant,
	ICommonLodging,
	ICommonRecommender,
	IContractedForeignBusiness,
	IFranchiseItem,
	IFranchiseListItem,
	IProgram,
	IRecommenderListItem,
	ISelectApplicantPopup,
	IWCFranchiseAssignmentItem,
	IWorkerProgram,
	IWorkerProviderLodgingItem,
	TCommonDistrict,
	TCommonGetFieldsResponse,
	TGetCommonListResponse,
	TGetListUserSettingResponse,
	TSystemUnitItem,
	TUpdateField,
	TUpdateFieldsRequest,
	WorkerLodgingListItem,
} from '@/services';
import {
	formatWorkerApplicantDetail,
	formatWorkerApplicantListItem,
	formatWorkerFranchiseDetail,
	formatWorkerFranchiseListItem,
	formatWorkerLodgingDetail,
	formatWorkerLodgingListDetail,
	formatWorkerProgramDetail,
	formatWorkerProgramListItem,
	IWorkerApplicantDetailStore,
	IWorkerFranchiseDetailStore,
	IWorkerLodgingDetailStore,
	IWorkerProgramDetailStore,
} from '@/stores';
import {
	formatWorkerRecommenderDetail,
	formatWorkerRecommenderListItem,
} from '@/stores/worker-recommender/worker-recommender.helper';
import {
	convertGenderCodeToName,
	convertNationCodeToName,
	convertUnitTypeCodeToName,
	convertVisaCodeToName,
	formatDate,
	map,
	removeTableNameFromString,
	showDataGridWithSymbol,
	toCamelCase,
} from '@/utils';

import { DefaultOptionType } from 'antd/es/select';

import { keys, reduce } from 'ramda';
import { isArray, isEmptyArray, isNotNilOrEmpty } from 'ramda-adjunct';

import { IWorkerRecommenderDetailStore } from '../worker-recommender';
import {
	IPayloadGetCommonListSuccess,
	IPayloadGetDocumentObjectSuccess,
	IPayloadSelectMasterInfoSuccess,
	IPayloadUpdateFieldsRequest,
	IPayloadUploadFileRequest,
	TReqUploadDocumentList,
} from './common.type';

export const formatSelectMasterDetail = (
	input: TGetCommonListResponse[number],
	type: IPayloadSelectMasterInfoSuccess['type'],
	index?: number
): DefaultOptionType => {
	const selectItem = formatCommonDetail(input, type);
	switch (type) {
		case MasterInfoEnum.UNIT:
		case MasterInfoEnum.DISTRICT:
		case MasterInfoEnum.FOREIGN_DOMESTIC_UNIT:
			return {
				label: selectItem?.name,
				value: selectItem?.id,
			};
		case MasterInfoEnum.PROGRAM:
			return {
				label: selectItem?.name,
				value: selectItem?.id,
				startDate: formatDate(selectItem?.startDate),
				endDate: formatDate(selectItem?.endDate),
			};
		case MasterInfoEnum.AUTOCOMPLETE_RECOMMENDER_NAME:
			return {
				label: selectItem,
				value: selectItem,
			};
		case MasterInfoEnum.AUTOCOMPLETE_FRANCHISE_EMPLOYER_NAME:
			return {
				label: selectItem?.employerName,
				value: selectItem?.employerName,
			};
		case MasterInfoEnum.ASSIGNMENT_APPLICANT:
			return {
				label: selectItem?.id,
				value: selectItem?.id,
			};
		case MasterInfoEnum.APPLICANT_BY_FRANCHISE:
			return {
				label: `${selectItem?.firstName} ${selectItem?.lastName}`,
				value: selectItem?.id,
			};
		case MasterInfoEnum.APPLICANT_BY_PROGRAM:
			return {
				label: selectItem?.fullName,
				value: selectItem?.id,
			};
		case MasterInfoEnum.AUTOCOMPLETE_PROVIDER_NAME:
			return {
				label: selectItem?.providerName,
				value: selectItem?.providerName,
			};
		case MasterInfoEnum.AUTOCOMPLETE_ADDRESS:
			return {
				label: selectItem?.value,
				value: selectItem?.id,
			};
		case MasterInfoEnum.AUTOCOMPLETE_APPLICANT_NAME:
			return {
				label: selectItem?.value,
				value: selectItem?.value,
			};
		case MasterInfoEnum.AUTOCOMPLETE_FRANCHISE:
			return {
				label: selectItem?.value,
				value: selectItem?.value,
			};
		case MasterInfoEnum.AUTOCOMPLETE_RECOMMENDER:
			return {
				label: selectItem?.value,
				value: selectItem?.value,
			};
		case MasterInfoEnum.PUBLIC_FRANCHISE_AGRICULTURE:
			return {
				label: selectItem?.name,
				value: selectItem?.id,
			};
		case MasterInfoEnum.AUTO_COMPLETE_MANAGER_NAME:
			return {
				label: selectItem,
				value: selectItem,
			};
		case MasterInfoEnum.FRANCHISE_APPLICANT_TO_ASSIGNED:
			return {
				label: selectItem?.name,
				value: selectItem?.id,
			};
		case MasterInfoEnum.APPLICANT_SALARY_AUTOCOMPLETE_APPLICANT_NAME:
			return {
				label: selectItem?.name,
				value: selectItem?.id,
			};
		case MasterInfoEnum.APPLICANT_SALARY_AUTOCOMPLETE_EMPLOYEE_NAME:
			return {
				label: selectItem?.name,
				value: selectItem?.id,
			};
		case MasterInfoEnum.COMMON_TARGET_UNIT:
			return {
				label: selectItem?.name,
				value: selectItem?.id,
			};
		case MasterInfoEnum.AUTOCOMPLETE_QUESTIONER:
			return {
				label: selectItem,
				value: selectItem,
			};
		case MasterInfoEnum.FOREIGN_PROGRAM_LIST:
			return {
				label: selectItem?.programForeignName,
				value: selectItem?.programForeignId,
			};
		case MasterInfoEnum.DOMESTIC_UNIT_DROPDOWN:
			return {
				label: selectItem?.name,
				value: selectItem?.id,
			};
		case MasterInfoEnum.COMMON_NOTICE_RECEIVE_REGISTER_UNIT:
			return {
				label: selectItem?.name,
				value: selectItem?.id,
			};
		case MasterInfoEnum.COMMON_UNIT_DOMESTIC_REGISTER_LIST:
			return {
				label: selectItem?.name,
				value: selectItem?.id,
			};
		case MasterInfoEnum.AUTOCOMPLETE_FRANCHISE_NAME:
			return {
				label: selectItem?.name,
				value: selectItem?.id,
			};
		case MasterInfoEnum.COMMON_DISTRICT_VALIDATOR:
			return {};
		case MasterInfoEnum.PUBLIC_FRANCHISE_EMPLOYER_NAME:
			return {
				label: selectItem?.name,
				value: selectItem?.id,
			};
		case MasterInfoEnum.USER_AUTOCOMPLETE_SUGGESTIONS:
		case MasterInfoEnum.DEVICE_LOG_ADMIN_SUGGESTIONS:
			return {
				label: selectItem?.value,
				value: selectItem?.id,
			};
		default:
			return {
				label: selectItem.name,
				value: selectItem.id.toString(),
			};
	}
};

export const formatSelectMasterList = (
	input: TGetCommonListResponse,
	type: IPayloadSelectMasterInfoSuccess['type']
) => {
	if (!isArray(input)) return [];
	if (type === MasterInfoEnum.AUTOCOMPLETE_PROVIDER_NAME) {
		return (input as IAutocompleteSuggestionItem[])
			.filter((item) => item?.providerName)
			.map((value, index) => formatSelectMasterDetail(value, type, index));
	}

	return input?.map((value, index: number, array) =>
		formatSelectMasterDetail(value, type, index)
	);
};

export const formatCommonDetail = (
	input: TGetCommonListResponse[number],
	type: IPayloadGetCommonListSuccess['type'],
	index?: number
) => {
	let _input = typeof input === 'string' ? input : { ...input };
	switch (type) {
		case MasterInfoEnum.UNIT:
			return _input as TSystemUnitItem;
		case MasterInfoEnum.PROGRAM:
			return {
				...(_input as IProgram),
				startDate: formatDate((_input as IProgram).startDate),
				endDate: formatDate((_input as IProgram).endDate),
				no: index,
			};
		case MasterInfoEnum.DISTRICT:
			return _input as TCommonDistrict;
		case MasterInfoEnum.FRANCHISE:
		case MasterInfoEnum.FRANCHISE_DROPDOWN:
		case MasterInfoEnum.RECOMMENDER_FRANCHISE:
			return _input as IFranchiseItem;
		case MasterInfoEnum.ASSIGNMENT_APPLICANT:
			return {
				...(_input as ICommonAssignmentApplicant),
				assignmentFranchiseStartEndDate: showDataGridWithSymbol(
					formatDate(
						(_input as ICommonAssignmentApplicant).assignmentFranchiseStartDate
					),
					formatDate(
						(_input as ICommonAssignmentApplicant).assignmentFranchiseEndDate
					),
					'~'
				),
				registDt: formatDate((_input as ICommonAssignmentApplicant).registDt),
				applicantBirthDate: formatDate(
					(_input as ICommonAssignmentApplicant).applicantBirthDate
				),
				franchiseEmployerBirthDate: formatDate(
					(_input as ICommonAssignmentApplicant).franchiseEmployerBirthDate
				),
				workStartEndDate: showDataGridWithSymbol(
					formatDate((_input as ICommonAssignmentApplicant).workStartDate),
					formatDate((_input as ICommonAssignmentApplicant).workEndDate),
					'~'
				),
			};
		case MasterInfoEnum.WC_FRANCHISE_ASSIGNMENT: {
			const typedInput = _input as IWCFranchiseAssignmentItem;
			return {
				...typedInput,
				startDate: formatDate(typedInput.startDate),
				endDate: formatDate(typedInput.endDate),
				startEndDate: showDataGridWithSymbol(
					formatDate(typedInput.startDate),
					formatDate(typedInput.endDate),
					'~'
				),
				registDt: formatDate(typedInput.registDt),
			};
		}
		case MasterInfoEnum.APPLICANT:
			return {
				...(_input as IAssignmentItem),
				workStartEndDate: showDataGridWithSymbol(
					formatDate((_input as IAssignmentItem).workStartDate),
					formatDate((_input as IAssignmentItem).workEndDate),
					'~'
				),
			};

		case MasterInfoEnum.APPLICANT_BY_FRANCHISE:
		case MasterInfoEnum.APPLICANT_BY_PROGRAM:
		case MasterInfoEnum.APPLICANT_BY_LODGING:
		case MasterInfoEnum.APPLICANT_BY_RECOMMENDER: {
			const typedInput = _input as ISelectApplicantPopup;

			return {
				...typedInput,
				visaName: convertVisaCodeToName(typedInput?.visaCode),
				birthDate: formatDate(typedInput.birthDate),
				entryDate: formatDate(typedInput.entryDate),
				relationship: '',
			};
		}

		case MasterInfoEnum.PROVIDER_LODGING: {
			return {
				...(_input as IWorkerProviderLodgingItem),
				birthDate: formatDate((_input as IWorkerProviderLodgingItem).birthDate),
				registDt: formatDate((_input as IWorkerProviderLodgingItem).registDt),
			};
		}
		case MasterInfoEnum.COMMON_ASSIGNMENT_AUTO: {
			return {
				...(_input as ICommonApplicant),
				dateEntryAndDeparture: showDataGridWithSymbol(
					formatDate((_input as ICommonApplicant).entryDate),
					formatDate((_input as ICommonApplicant).departureDate)
				),

				genderName: convertGenderCodeToName(
					(_input as ICommonApplicant).gender
				),
				heightPerWeight: showDataGridWithSymbol(
					(_input as ICommonApplicant).height,
					(_input as ICommonApplicant).weight
				),
				workHistory: (_input as ICommonApplicant).id,
				disabled: (_input as ICommonApplicant).canAssign === false,
			};
		}
		case MasterInfoEnum.COMMON_LODGING: {
			return {
				...(_input as ICommonLodging),
				ownershipTypeName: OWNERSHIP_OPTIONS.find(
					(e: DefaultOptionType) =>
						e.value === (_input as ICommonLodging)?.ownershipType
				)?.label,
				facilityTypeName: INFRASTRUCTURE_OPTIONS.find(
					(e: DefaultOptionType) =>
						e.value === (_input as ICommonLodging)?.facilityType
				)?.label,
			};
		}
		case MasterInfoEnum.COMMON_RECOMMENDER: {
			return {
				...(_input as ICommonRecommender),
				typeName: DIVISION_RECOMMENDER_OPTIONS.find(
					(e: DefaultOptionType) =>
						e.value === (_input as ICommonRecommender)?.type
				)?.label,
				registDt: formatDate((_input as IWorkerProviderLodgingItem).registDt),
			};
		}
		case MasterInfoEnum.FOREIGN_BUSINESS_UNIT: {
			return {
				...(_input as unknown as IContractedForeignBusiness),
				typeName: convertUnitTypeCodeToName(
					(_input as unknown as IContractedForeignBusiness).type
				),
				registDt: formatDate(
					(_input as unknown as IContractedForeignBusiness).registDt
				),
				nationalName: convertNationCodeToName(
					(_input as unknown as IContractedForeignBusiness).nationalCode
				),
				no: index,
			};
		}
		default:
			return _input as any;
	}
};

export const formatCommonList = (
	input: TGetCommonListResponse,
	type: IPayloadGetCommonListSuccess['type']
) => {
	if (!isArray(input)) return [];
	return input?.map((value, index: number, array) =>
		formatCommonDetail(value, type, input?.length - index)
	);
};

export const formatUploadRequest = ({
	reqUploadDocumentList,
	refTableName,
	refTableId,
}: IPayloadUploadFileRequest['data']) => {
	const formData = new FormData();
	reqUploadDocumentList.forEach(
		(file: TReqUploadDocumentList, index: number) => {
			formData.append(
				`reqUploadDocumentList[${index}].mutipartFile`,
				file.mutipartFile
			);
			formData.append(
				`reqUploadDocumentList[${index}].fileType`,
				file.fileType
			);

			file.documentType &&
				formData.append(
					`reqUploadDocumentList[${index}].documentType`,
					file.documentType
				);
		}
	);
	if (refTableName) formData.append(`refTableName`, refTableName);
	formData.append(`refTableId`, refTableId || '');

	return formData;
};

const toUpperCaseFieldName = (name: string) =>
	name
		.replace(/([a-z])([A-Z])/g, '$1_$2')
		.replace(/([A-Z])([A-Z])/g, '$1_$2')
		.replace(/([a-z])([0-9])/g, '$1_$2')
		.toUpperCase();

export const formatUpdateFieldsRequest = ({
	objectName,
	id,
	data,
}: IPayloadUpdateFieldsRequest['data']): TUpdateFieldsRequest => {
	const updateFields: TUpdateField[] = [];
	Object.keys(data).map((e: string) => {
		if (e !== 'id') {
			updateFields.push({
				name: toUpperCaseFieldName(e) as TUpdateField['name'],
				value: isNotNilOrEmpty(data[e]) ? data[e] : null,
			});
		}
	});

	return {
		objectName,
		id,
		updateFields,
	};
};

export const formatColumnNameResponse = (
	input: TGetListUserSettingResponse
): TGetListUserSettingResponse => {
	if (!input && isEmptyArray(input)) return [];

	const content: TGetListUserSettingResponse = [];

	Object.values(input).forEach((value, index) => {
		const objectName = value?.objectName;
		const fields = value?.fields;
		let camelFields: string[] = [];

		fields?.forEach((field) => {
			let newField = `${objectName}_${field}`
				?.toLowerCase()
				.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
				.replace(/_([0-9])/g, (_, letter) => letter);
			camelFields.push(newField);
		});

		content.push({
			objectName,
			fields: camelFields,
		});
	});

	return content;
};

export const formatDocumentObjectDetail = (
	input: GetCommonDocumentObjectListResponse[number],
	refTableName: IPayloadGetDocumentObjectSuccess['refTableName']
) => {
	let _input = { ...input };
	switch (refTableName) {
		case DocumentObjectListRefTableEnum.APPLICANT:
			return formatWorkerApplicantListItem(_input as IApplicantListItem);
		case DocumentObjectListRefTableEnum.FRANCHISE:
			return formatWorkerFranchiseListItem(_input as IFranchiseListItem);
		case DocumentObjectListRefTableEnum.LODGING:
			return formatWorkerLodgingListDetail(_input as WorkerLodgingListItem);
		case DocumentObjectListRefTableEnum.RECOMMENDER:
			return formatWorkerRecommenderListItem(_input as IRecommenderListItem);
		case DocumentObjectListRefTableEnum.PROGRAM:
			return formatWorkerProgramListItem(_input as IWorkerProgram);
		default:
			return _input as any;
	}
};

export const formatDocumentObjectList = (
	input: GetCommonDocumentObjectListResponse,
	refTableName: IPayloadGetDocumentObjectSuccess['refTableName']
) => {
	if (!isArray(input)) return [];
	return input?.map((value, index: number, array) =>
		formatDocumentObjectDetail(value, refTableName)
	);
};

export const formatUpdateHistoryUnit = (
	input: GetUpdateHistoryListResponse['content'][number]
) => {
	return {
		prevData: convertUnitTypeCodeToName(input.prevData as string),
		afterData: convertUnitTypeCodeToName(input.afterData as string),
	};
};

export const formatUpdateHistoryDetail = (
	input: GetUpdateHistoryListResponse['content'][number],
	index: number,
	params: {
		total: number;
		page: number;
		pageSize: number;
	}
) => {
	const formattedData = {
		...input,
		prevData: input?.prevData,
		afterData: input?.afterData,
		registDt: formatDate(input.registDt),
		no: params.total - (params.page - 1) * params.pageSize - index,
	};

	switch (input.refTableName) {
		case UpdateHistoryRefTableNameEnum.UNIT:
			return {
				...formattedData,
				...formatUpdateHistoryUnit(formattedData),
			};
		//TODO: add more case here
		default:
			return formattedData;
	}
};

export const formatUpdateHistoryList = (
	input: GetUpdateHistoryListResponse['content'],
	params: {
		total: number;
		page: number;
		pageSize: number;
	}
) => {
	return map(input, (value, index) =>
		formatUpdateHistoryDetail(value, index, params)
	);
};

type DocumentObjectType<T extends DocumentObjectListRefTableEnum> =
	T extends DocumentObjectListRefTableEnum.APPLICANT
		? IWorkerApplicantDetailStore
		: T extends DocumentObjectListRefTableEnum.FRANCHISE
			? IWorkerFranchiseDetailStore
			: T extends DocumentObjectListRefTableEnum.LODGING
				? IWorkerLodgingDetailStore
				: T extends DocumentObjectListRefTableEnum.RECOMMENDER
					? IWorkerRecommenderDetailStore
					: T extends DocumentObjectListRefTableEnum.PROGRAM
						? IWorkerProgramDetailStore
						: never;

export const formatDocumentInput = (data: any, tableName: string) => {
	let _data = { id: data.id };
	Object.keys(data).map((e: string) => {
		if (e !== 'id') {
			let key = removeTableNameFromString(e, tableName);
			_data = {
				..._data,
				[key]: data[e],
			};
		}
	});

	return _data;
};

export const formatWorkerDocumentObjectDetail = <
	T extends DocumentObjectListRefTableEnum,
>(
	input: any,
	refTableName: T
): DocumentObjectType<T> => {
	let _input = formatDocumentInput(input, refTableName) as any;
	switch (refTableName) {
		case DocumentObjectListRefTableEnum.APPLICANT:
			return formatWorkerApplicantDetail(_input) as DocumentObjectType<T>;
		case DocumentObjectListRefTableEnum.FRANCHISE:
			return formatWorkerFranchiseDetail(_input) as DocumentObjectType<T>;
		case DocumentObjectListRefTableEnum.LODGING:
			return formatWorkerLodgingDetail(_input) as DocumentObjectType<T>;
		case DocumentObjectListRefTableEnum.RECOMMENDER:
			return formatWorkerRecommenderDetail(_input) as DocumentObjectType<T>;
		case DocumentObjectListRefTableEnum.PROGRAM:
			return formatWorkerProgramDetail(_input) as DocumentObjectType<T>;
		default:
			throw new Error(`Unsupported refTableName: ${refTableName}`);
	}
};

export const formatCommonApplicantDetail = (
	input: GetCommonApplicantListResponse['content'][number]
) => {
	return {
		...input,
		dateEntryAndDeparture: showDataGridWithSymbol(
			formatDate(input.entryDate),
			formatDate(input.departureDate)
		),
		registDt: formatDate(input.registDt),
		genderName: convertGenderCodeToName(input.gender),
		heightPerWeight: showDataGridWithSymbol(input.height, input.weight),
		workHistory: input.id,
		disabled: input.canAssign === false,
	};
};

export const formatCommonApplicantList = (
	input: GetCommonApplicantListResponse['content']
) => {
	return map(input, formatCommonApplicantDetail);
};

export const formatCommonApplicantAssignmentDetail = (
	input: GetCommonAssignmentApplicantListResponse['content'][number]
) => {
	return {
		...input,
		dateEntryAndDeparture: showDataGridWithSymbol(
			formatDate(input.entryDate),
			formatDate(input.departureDate)
		),

		genderName: convertGenderCodeToName(input.gender),
		heightPerWeight: showDataGridWithSymbol(input.height, input.weight),
		workHistory: input.id,
		disabled: false,
	};
};

export const formatCommonApplicantAssignmentList = (
	input: GetCommonAssignmentApplicantListResponse['content']
) => {
	return map(input, formatCommonApplicantAssignmentDetail);
};

export const formatCommonGetFields = (input: TCommonGetFieldsResponse) => {
	return map(input, (item) => {
		const { ID, ...rest } = item;

		const camelCasedRest = reduce(
			(acc, key: string) => ({
				...acc,
				[toCamelCase(key)]: rest[key],
			}),
			{},
			keys(rest) as string[]
		);

		return {
			id: ID,
			fields: camelCasedRest,
		};
	});
};

export const formatAppUserInfo = (input: GetAppUserInfoResponse) => {
	const formatNotiRegistDt = input.notiRegistDt
		? `, 변경일: ${
				input.notiRegistDt
					? formatDate(input.notiRegistDt, {
							outputFormat: 'YYYY-MM-DD HH:mm',
						})
					: '-'
			}`
		: '';
	const formatExpApplicantNotiRegistDt = input.expApplicantNotiRegistDt
		? `, 변경일: ${
				input.expApplicantNotiRegistDt
					? formatDate(input.expApplicantNotiRegistDt, {
							outputFormat: 'YYYY-MM-DD HH:mm',
						})
					: '-'
			}`
		: '';
	return {
		...input,
		registDt: input.registDt ? formatDate(input.registDt) : '-',
		lastestLoggedInDt: input.lastestLoggedInDt
			? formatDate(input.lastestLoggedInDt, {
					outputFormat: 'YYYY-MM-DD HH:mm',
				})
			: '-',
		notiRegistDt: `${input.activatedNoti ? 'ON' : 'OFF'}${formatNotiRegistDt}`,
		expApplicantNotiRegistDt: `${input.activatedExpApplicantNoti ? 'ON' : 'OFF'}${formatExpApplicantNotiRegistDt}`,
		agreementDt: input?.agreementDt ? formatDate(input.agreementDt) : '-',
	};
};

export const formatDocumentMappings = (input: GetDocumentMappingsResponse) => {
	const result: Record<string, DefaultOptionType[]> = {};
	const seen: Record<string, Set<number>> = {};

	input.forEach((item) => {
		const refTableName = item.refTableName;
		const idKey = item.refTableId;

		if (!result[refTableName]) {
			result[refTableName] = [];
			seen[refTableName] = new Set<number>();
		}

		if (!seen[refTableName].has(idKey)) {
			result[refTableName].push({
				label: item.refName,
				value: idKey,
			});
			seen[refTableName].add(idKey);
		}
	});

	return result;
};
